import React from 'react';

import classNames from 'classnames';

import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';
import type { ValidUniversalToothId } from '@videa/utils/src/normalizeToothIds';

import { Tooth } from '../../../patientList/patientStrip/patientStripOdontogram/Tooth';

interface ToothQuadrantProps {
  startNumber: number | string;
  position: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  isPrimary?: boolean;
  toothCount?: number;
  onToothClick?: (toothId: ValidUniversalToothId) => void;
  selectedTooth: Set<ValidUniversalToothId>;
}

const getToothSize = (toothNumber: number | string) => {
  // For primary teeth (A-T), use consistent size
  if (typeof toothNumber === 'string') {
    return { width: 2, height: 2 };
  }

  // Back teeth (1-3, 14-19, 30-32)
  if (
    (toothNumber >= 1 && toothNumber <= 3) ||
    (toothNumber >= 14 && toothNumber <= 19) ||
    (toothNumber >= 30 && toothNumber <= 32)
  ) {
    return { width: 2, height: 2 };
  }
  // Middle teeth (4-5, 12-13, 20-21, 28-29)
  if (
    (toothNumber >= 4 && toothNumber <= 5) ||
    (toothNumber >= 12 && toothNumber <= 13) ||
    (toothNumber >= 20 && toothNumber <= 21) ||
    (toothNumber >= 28 && toothNumber <= 29)
  ) {
    return { width: 2, height: 2 };
  }
  // Front teeth (6-11, 22-27)
  return { width: 2, height: 2 };
};

export const ToothQuadrant: React.FC<ToothQuadrantProps> = ({
  startNumber,
  position,
  isPrimary = false,
  toothCount = 8,
  onToothClick,
  selectedTooth,
}) => {
  const { displayToothNumber } = useUniversalToFDI();

  const getAlignmentClasses = () => {
    switch (position) {
      case 'topLeft':
        return 'vai-justify-end vai-items-end';
      case 'topRight':
        return 'vai-justify-start vai-items-end';
      case 'bottomLeft':
        return 'vai-justify-end vai-items-start';
      case 'bottomRight':
        return 'vai-justify-start vai-items-start';
    }
  };

  const getToothId = (index: number): ValidUniversalToothId => {
    if (isPrimary) {
      const letters = [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
      ];
      const startIndex = letters.indexOf(startNumber as string);
      // For bottom quadrants, reverse the sequence
      if (position.startsWith('bottom')) {
        return letters[startIndex - index] as ValidUniversalToothId;
      }
      return letters[startIndex + index] as ValidUniversalToothId;
    }
    // For permanent teeth
    const startNum = typeof startNumber === 'number' ? startNumber : 0;
    if (position.startsWith('bottom')) {
      return (startNum - index).toString() as ValidUniversalToothId;
    }
    return (startNum + index).toString() as ValidUniversalToothId;
  };

  return (
    <div
      className={classNames(
        'vai-flex vai-flex-wrap vai-gap-0.5 vai-p-0.5',
        getAlignmentClasses(),
      )}
    >
      {Array.from({ length: toothCount }).map((_, index) => {
        const toothId = getToothId(index);
        const size = getToothSize(toothId);
        const isSelected = selectedTooth.has(toothId);
        const displayNumber = displayToothNumber(toothId);

        return (
          <button
            key={`tooth-${toothId}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToothClick?.(toothId);
            }}
            className={classNames(
              'vai-relative vai-group vai-flex vai-items-center vai-justify-center vai-p-1 vai-rounded vai-transition-colors',
              isSelected ? 'vai-bg-blue-100' : 'hover:vai-bg-gray-200',
            )}
          >
            <div className="vai-relative">
              <Tooth
                type="normal"
                toothId={toothId}
                hx={size.height}
                wx={size.width}
              />
              <div
                className={classNames(
                  'vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center vai-text-xs vai-font-medium',
                  isSelected
                    ? 'vai-opacity-100'
                    : 'vai-opacity-0 group-hover:vai-opacity-100',
                  position.startsWith('top')
                    ? 'vai-translate-y-[14px]'
                    : 'vai-translate-y-[-14px]',
                )}
              >
                <span className="vai-text-gray-800 vai-font-semibold">
                  {displayNumber}
                </span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
